import { Badge, TextField, Typography } from "@mui/material";
import { Column } from "../../misc/Column";
import {
  FreeTextTheoryQuestion,
  ImageChoiceTheoryQuestion,
  MultipleChoiceTheoryQuestion,
  TheoryQuestion,
} from "../../model/TheoryQuestion";
import { GivenAnswer } from "../../providers/theoryExamSimulationResultsProvider";
import { Row } from "../../misc/Row";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";

interface TheoryQuestionViewProps {
  question: TheoryQuestion;
  givenAnswer: GivenAnswer;
}
export function TheoryQuestionView({ question, givenAnswer }: TheoryQuestionViewProps) {
  if (question.typ === "Multiple-Choice-Frage") {
    return <_MultipleChoiceQuestionView question={question} givenAnswer={givenAnswer} />;
  } else if (question.typ === "Zahlenfrage") {
    return <_FreeTextQuestionView question={question} givenAnswer={givenAnswer} />;
  } else if (question.typ === "Bildauswahlfrage") {
    return <_ImageChoiceQuestionView question={question} givenAnswer={givenAnswer} />;
  }

  return null;
}

const _MultipleChoiceQuestionView = ({
  question,
  givenAnswer,
}: {
  question: MultipleChoiceTheoryQuestion;
  givenAnswer: GivenAnswer;
}) => {
  return (
    <Column spacing={2}>
      <Typography variant="h6">{question.title}</Typography>
      <Row spacing={2} alignItems="flex-start">
        {question.image && (
          <Column>
            <img src={question.image.url} width={640} height={384} />
            <Typography variant="caption">{question.imageCaption ?? ""}</Typography>
          </Column>
        )}
        {question.video && <video src={question.video.url} width={640} height={384} controls />}
        <Column spacing={1} width="100%" maxWidth="640px">
          <Typography variant="body1">{question.subtitle}</Typography>
          {givenAnswer.shuffledOptions?.map((optionIndex) => (
            <_Option
              key={optionIndex}
              isCorrect={question.options[optionIndex].isCorrect}
              isSelected={givenAnswer.selectedOptions?.includes(optionIndex) ?? false}
              text={question.options[optionIndex].text}
            />
          ))}
        </Column>
      </Row>
    </Column>
  );
};

const _Option = ({ isCorrect, isSelected, text }: { isCorrect: boolean; isSelected: boolean; text: string }) => {
  return (
    <Row
      spacing={1.5}
      alignItems="center"
      borderRadius="8px"
      border="1px solid"
      borderColor={isCorrect === isSelected ? "primary.main" : "error.main"}
      p={1}
    >
      {isSelected ? <_SelectedCheckBox isCorrect={isCorrect} /> : <_UnselectedCheckBox isCorrect={isCorrect} />}
      <Typography variant="body2">{text}</Typography>
    </Row>
  );
};

const _SelectedCheckBox = ({ isCorrect }: { isCorrect: boolean }) => {
  return (
    <_SolutionCheckBox isCorrect={isCorrect}>
      <CheckBoxRoundedIcon sx={{ color: "secondary.main" }} fontSize="large" />
    </_SolutionCheckBox>
  );
};

const _UnselectedCheckBox = ({ isCorrect }: { isCorrect: boolean }) => {
  return (
    <_SolutionCheckBox isCorrect={isCorrect}>
      <CheckBoxOutlineBlankRoundedIcon sx={{ color: "grey.300" }} fontSize="large" />
    </_SolutionCheckBox>
  );
};

const _SolutionCheckBox = ({ isCorrect, children }: { isCorrect: boolean; children: React.ReactNode }) => {
  return (
    <Badge
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      badgeContent={
        isCorrect ? (
          <CheckBoxRoundedIcon sx={{ color: "primary.main" }} fontSize="small" />
        ) : (
          <CheckBoxOutlineBlankRoundedIcon sx={{ color: "primary.main" }} fontSize="small" />
        )
      }
      sx={{
        "& .MuiBadge-badge": {
          bottom: "16%",
          right: "16%",
          backgroundColor: "common.white",
          padding: 0,
          borderRadius: "4px",
        },
      }}
    >
      {children}
    </Badge>
  );
};

const _FreeTextQuestionView = ({
  question,
  givenAnswer,
}: {
  question: FreeTextTheoryQuestion;
  givenAnswer: GivenAnswer;
}) => {
  return (
    <Column spacing={2}>
      <Typography variant="h6">{question.title}</Typography>
      <Row spacing={2} alignItems="flex-start">
        {question.image && (
          <Column>
            <img src={question.image.url} width={640} height={384} />
            <Typography variant="caption">{question.imageCaption ?? ""}</Typography>
          </Column>
        )}
        {question.video && <video src={question.video.url} width={640} height={384} controls />}
        <Column spacing={1} width="100%" maxWidth="640px">
          <Typography variant="body1">{question.subtitle}</Typography>
          {givenAnswer.enteredAnswers && (
            <_FreeTextAnswer
              isCorrect={givenAnswer.hasBeenAnsweredCorrectly}
              answers={givenAnswer.enteredAnswers.map(formatEnteredAnswer)}
              textWithPlaceholders={question.textWithPlaceholders}
            />
          )}
          {!givenAnswer.hasBeenAnsweredCorrectly && (
            <Column spacing={1}>
              <Typography variant="body1">LÖSUNG:</Typography>
              <_FreeTextAnswer
                isCorrect={true}
                answers={question.correctAnswers.map((answer) => answer.toString())}
                textWithPlaceholders={question.textWithPlaceholders}
              />
            </Column>
          )}
        </Column>
      </Row>
    </Column>
  );
};

const _FreeTextAnswer = ({
  isCorrect,
  answers,
  textWithPlaceholders,
}: {
  answers: string[];
  isCorrect: boolean;
  textWithPlaceholders: string;
}) => {
  const textSegments = textWithPlaceholders.split(" ___ ");
  const mergedElements: React.ReactNode[] = [];
  for (let i = 0; i < textSegments.length; i++) {
    if (textSegments[i]) {
      mergedElements.push(
        <Typography key={`text-${i}`} variant="body2" component="span">
          {textSegments[i]}
        </Typography>,
      );
    }
    if (i < answers.length && answers[i]) {
      mergedElements.push(
        <TextField
          key={`answer-${i}`}
          value={answers[i]}
          InputProps={{ readOnly: true }}
          sx={{
            borderRadius: "8px",
            "& .MuiInputBase-root": {
              minWidth: "20px",
              width: `${answers[i].length * 7 + 20}px`,
              color: isCorrect ? "primary.main" : "error.main",
            },
            "& input": {
              padding: "4px 8px",
            },
          }}
        />,
      );
    }
  }

  return (
    <Row spacing={0.5} alignItems="center" flexWrap="wrap">
      {mergedElements}
    </Row>
  );
};

// visible for testing
export function formatEnteredAnswer(enteredAnswer?: string): string {
  if (!enteredAnswer) return "";
  return enteredAnswer
    .replace(/[^0-9,.]/g, "")
    .replace(/[.,]+$/, "")
    .replace(/\./g, ",")
    .replace(/,0+$/, "")
    .replace(/,+$/, "");
}

const _ImageChoiceQuestionView = ({
  question,
  givenAnswer,
}: {
  question: ImageChoiceTheoryQuestion;
  givenAnswer: GivenAnswer;
}) => {
  return (
    <Column spacing={2}>
      <Typography variant="h6">{question.title}</Typography>
      <Row spacing={2} alignItems="flex-start">
        {question.image && (
          <Column>
            <img src={question.image.url} width={640} height={384} />
            <Typography variant="caption">{question.imageCaption ?? ""}</Typography>
          </Column>
        )}
        {question.video && <video src={question.video.url} width={640} height={384} controls />}
        <Column spacing={1} width="100%">
          <Typography variant="body1">{question.subtitle}</Typography>
          <Row spacing={2} alignItems="center" justifyContent="space-between">
            {givenAnswer.shuffledOptions?.map((optionIndex) => (
              <_ImageChoiceOption
                key={optionIndex}
                isCorrect={question.options[optionIndex].isCorrect}
                isSelected={givenAnswer.selectedOptions?.includes(optionIndex) ?? false}
                imageUrl={question.options[optionIndex].image.url}
                imageWidth={question.options[optionIndex].imageWidth}
                imageHeight={question.options[optionIndex].imageHeight}
              />
            ))}
          </Row>
        </Column>
      </Row>
    </Column>
  );
};

const _ImageChoiceOption = ({
  isCorrect,
  isSelected,
  imageUrl,
  imageWidth,
  imageHeight,
}: {
  isCorrect: boolean;
  isSelected: boolean;
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
}) => {
  return (
    <Column
      spacing={1.5}
      alignItems="center"
      borderRadius="8px"
      border="1px solid"
      borderColor={isCorrect === isSelected ? "primary.main" : "error.main"}
      p={1}
    >
      <img src={imageUrl} width={imageWidth / 5} height={imageHeight / 5} />
      {isSelected ? <_SelectedCheckBox isCorrect={isCorrect} /> : <_UnselectedCheckBox isCorrect={isCorrect} />}
    </Column>
  );
};
